import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/store';
// 定义Toast的类型
export type ToastStatus = 'success' | 'error';
export type ToastProps = {
  message: string;
  status: ToastStatus;
  duration?: number;
};

// Toast服务的接口
interface ToastService {
  show: (props: ToastProps) => void;
  hide: () => void;
}

// Toast组件
const ToastComponent: React.FC<ToastProps & { onClose: () => void }> = ({ message, status, onClose }) => {
  const { fwClass } = useAppStore();
  // 根据状态选择图标
  const renderIcon = () => {
    if (status === 'success') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
          <circle cx="11.9998" cy="12.4764" r="10.0038" fill="#45A77B" />
          <path d="M8.03172 12.5297L10.5802 15.2939L16.4366 9.54395" stroke="white" strokeWidth="1.5" />
        </svg>
      );
    } else {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
          <circle cx="17.9998" cy="17.9998" r="15.0056" fill="#F06465" />
          <path d="M13.353 13.5541L22.3529 22.5541M22.3529 13.5541L13.353 22.5541" stroke="white" strokeWidth="2.25" strokeLinejoin="round" />
        </svg>
      );
    }
  };

  return (
    <div
      className="bg-white dark:bg-[#27272A] text-primaryTextColor border-[1px] border-borderColor rounded-[12px] px-[12px] w-[96vw] flex items-center justify-between h-[48px] z-[100] fixed top-[10px] left-[50%] translate-x-[-50%] animate-slideInDown"
      style={{
        transition: 'opacity 0.3s, transform 0.3s',
      }}
    >
      <div className="flex items-center gap-x-[12px]">
        {renderIcon()}
        <span className={cn('text-[14px]', fwClass)}>{message}</span>
      </div>
      <X className="text-[#09090B80] dark:text-[#FAFAFA80] cursor-pointer" onClick={onClose} />
    </div>
  );
};

// 创建并导出Toast服务
const createToast = (): ToastService => {
  // 创建一个DOM容器
  let containerElement: HTMLDivElement | null = null;
  let root: Root | null = null;
  let timeoutId: NodeJS.Timeout | null = null;

  // 确保容器已创建
  const ensureContainer = () => {
    if (!containerElement) {
      containerElement = document.createElement('div');
      containerElement.id = 'toast-container';
      document.body.appendChild(containerElement);
      root = createRoot(containerElement);
    }
  };

  // 显示Toast
  const show = (props: ToastProps) => {
    ensureContainer();

    // 清除已有定时器
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // 渲染Toast
    root?.render(<ToastComponent {...props} onClose={() => hide()} />);

    // 设置自动关闭
    const duration = props.duration || 3000;
    timeoutId = setTimeout(() => {
      hide();
    }, duration);
  };

  // 隐藏Toast
  const hide = () => {
    if (root) {
      root.render(null);
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return { show, hide };
};

// 导出单例服务
export const toast = createToast();
